/**
 * Dog vocalisations. Two sources behind one API (`bark/whine/huff`):
 *
 * 1. Recorded clips, if present in `public/sounds/`:
 *      bark-1.m4a … bark-N.m4a, whine-1.m4a …, huff-1.m4a …  (also .mp3/.wav)
 *    Record Luna on the phone, trim to the sound, drop them in. They're picked
 *    at random with slight pitch variation. This is the real plan.
 * 2. A Web Audio synth fallback for clips that don't exist yet: a rough
 *    glottal source (sawtooth + pitch jitter + waveshaper) through three
 *    sweeping vocal-tract formants ("w-ow-f"), a noise onset and a breathy
 *    release, in a small room reverb. Placeholder quality by design.
 *
 * Browsers block audio until a user gesture: call `unlock()` from a tap.
 * (iOS also mutes Web Audio when the ring/silent switch is on silent.)
 */
type Kind = 'bark' | 'whine' | 'huff' | 'growl' | 'squirrel' | 'cat' | 'lap' | 'shake' | 'brush' | 'splash' | 'crunch' | 'pant' | 'thud' | 'howl' | 'sniff';
/** a decoded recording plus where the sound actually starts and how loud it is */
interface Clip {
  buffer: AudioBuffer;
  offset: number; // seconds of leading silence to skip
  gain: number; // normalisation so every clip peaks the same
}

function analyse(buffer: AudioBuffer): Clip {
  const d = buffer.getChannelData(0);
  let peak = 0;
  for (let i = 0; i < d.length; i++) peak = Math.max(peak, Math.abs(d[i]));
  const thr = peak * 0.05;
  let start = 0;
  while (start < d.length && Math.abs(d[start]) < thr) start++;
  const offset = Math.max(0, start / buffer.sampleRate - 0.01);
  return { buffer, offset, gain: peak > 0 ? 0.9 / peak : 1 };
}
const EXTS = ['m4a', 'mp3', 'wav'];

export class DogSounds {
  constructor(private folder = '') {}
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private clips: Record<Kind, Clip[]> = { bark: [], whine: [], huff: [], growl: [], squirrel: [], cat: [], lap: [], shake: [], brush: [], splash: [], crunch: [], pant: [], thud: [], howl: [], sniff: [] };
  private crunchBusyUntil = 0;
  private pantBusyUntil = 0;
  /** distance-based loudness, set by the body each frame */
  private distanceGain = 1;

  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** Create/resume the context. Must be called from inside a user gesture the first time. */
  unlock(): void {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      const ctx = (this.ctx = new Ctor());
      const sr = ctx.sampleRate;

      // master → "body" EQ → compressor → out, plus a small reverb send.
      // Phone speakers roll off below ~400 Hz, so a bark loses its chest; a
      // peaking boost around 450 Hz and a compressor put the weight back.
      this.master = ctx.createGain();
      this.master.gain.value = 0.8;
      const body = ctx.createBiquadFilter();
      body.type = 'peaking';
      body.frequency.value = 450;
      body.Q.value = 0.9;
      body.gain.value = 5;
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -18;
      comp.knee.value = 12;
      comp.ratio.value = 4;
      comp.attack.value = 0.004;
      comp.release.value = 0.15;
      this.master.connect(body).connect(comp).connect(ctx.destination);
      const ir = ctx.createBuffer(2, Math.floor(sr * 0.35), sr);
      for (let ch = 0; ch < 2; ch++) {
        const d = ir.getChannelData(ch);
        for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (sr * 0.08));
      }
      const verb = ctx.createConvolver();
      verb.buffer = ir;
      const wet = ctx.createGain();
      wet.gain.value = 0.1;
      this.master.connect(verb).connect(wet).connect(comp);

      // 1 s of white noise, reused for breath/texture/jitter
      this.noise = ctx.createBuffer(1, sr, sr);
      const d = this.noise.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;

      void this.loadClips();
    }
    if (this.ctx.state !== 'running') void this.ctx.resume();
  }

  /**
   * Probe public/sounds/<dog>/ for this dog's own recordings, else the shared
   * public/sounds/ set; stop at the first missing index.
   */
  private async loadClips(): Promise<void> {
    const ctx = this.ctx!;
    for (const kind of ['bark', 'whine', 'huff', 'growl', 'squirrel', 'cat', 'lap', 'shake', 'brush', 'splash', 'crunch', 'pant', 'thud', 'howl', 'sniff'] as Kind[]) {
      const dirs = this.folder ? [`sounds/${this.folder}/`, 'sounds/'] : ['sounds/'];
      for (const dir of dirs) {
      for (let i = 1; i <= 8; i++) {
        let found = false;
        for (const ext of EXTS) {
          try {
            const res = await fetch(`${dir}${kind}-${i}.${ext}`);
            if (!res.ok || !(res.headers.get('content-type') ?? '').startsWith('audio')) continue;
            this.clips[kind].push(analyse(await ctx.decodeAudioData(await res.arrayBuffer())));
            found = true;
            break;
          } catch {
            /* keep probing */
          }
        }
        if (!found) break;
      }
      if (this.clips[kind].length) break; // own clips found; don't mix in the shared ones
      }
    }
  }

  /** Louder when close to the listener. */
  setDistance(metres: number): void {
    this.distanceGain = Math.min(1, 2.5 / Math.max(1, metres));
  }

  private playClip(kind: Kind, t0: number, gain: number, rate: number): boolean {
    const list = this.clips[kind];
    if (!list.length || !this.ctx || !this.master) return false;
    const clip = list[Math.floor(Math.random() * list.length)];
    const src = this.ctx.createBufferSource();
    src.buffer = clip.buffer;
    src.playbackRate.value = rate;
    const g = this.ctx.createGain();
    g.gain.value = gain * clip.gain;
    src.connect(g).connect(this.master);
    src.start(t0, clip.offset);
    return true;
  }

  private noiseSource(t0: number, dur: number): AudioBufferSourceNode {
    const src = this.ctx!.createBufferSource();
    src.buffer = this.noise;
    src.start(t0, Math.random() * 0.5);
    src.stop(t0 + dur);
    return src;
  }

  /** `count` quick barks. intensity 0..1 (excitement), pitch multiplier ~0.8..1.2 */
  bark(count = 1, intensity = 1, pitch = 1, delay = 0): void {
    if (!this.ctx || !this.master || !this.noise) return;
    const ctx = this.ctx;
    for (let i = 0; i < count; i++) {
      const t0 = ctx.currentTime + delay + i * (0.22 + Math.random() * 0.08);
      const loud = (0.5 + 0.5 * intensity) * this.distanceGain;
      const p = pitch * (0.94 + Math.random() * 0.12);
      // recordings are pitched down a little: deeper reads as bigger and survives a phone speaker better
      if (this.playClip('bark', t0, loud, p * 0.82)) continue;

      // --- glottal source: sawtooth, f0 dips low on the "r" then rises into the "uff" and falls
      const f0 = 380 * p;
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(f0 * 0.45, t0);
      osc.frequency.exponentialRampToValueAtTime(f0 * 1.2, t0 + 0.045);
      osc.frequency.exponentialRampToValueAtTime(f0 * 0.85, t0 + 0.12);
      osc.frequency.exponentialRampToValueAtTime(f0 * 0.5, t0 + 0.2);
      // roughness: random low-frequency FM (growl), strongest on the onset
      const jit = this.noiseSource(t0, 0.25);
      const jitBp = ctx.createBiquadFilter();
      jitBp.type = 'bandpass';
      jitBp.frequency.value = 60;
      jitBp.Q.value = 0.7;
      const jitGain = ctx.createGain();
      jitGain.gain.setValueAtTime(f0 * 0.6, t0);
      jitGain.gain.linearRampToValueAtTime(f0 * 0.15, t0 + 0.12);
      jit.connect(jitBp).connect(jitGain).connect(osc.frequency);
      // harmonics: soft clip
      const shaper = ctx.createWaveShaper();
      const curve = new Float32Array(256);
      for (let k = 0; k < 256; k++) curve[k] = Math.tanh(((k / 255) * 2 - 1) * 3);
      shaper.curve = curve;
      osc.connect(shaper);

      // --- vocal tract: three formants sweeping "w" → "ah" → closing
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, t0);
      env.gain.exponentialRampToValueAtTime(loud, t0 + 0.015);
      env.gain.setValueAtTime(loud, t0 + 0.09);
      env.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.21);
      const formants: [number, number, number, number][] = [
        // [start Hz, peak Hz, Q, gain]
        [450, 850, 5, 1.0],
        [1100, 1650, 6, 0.55],
        [2500, 2800, 8, 0.25],
      ];
      for (const [fa, fb, q, g] of formants) {
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.Q.value = q;
        bp.frequency.setValueAtTime(fa, t0);
        bp.frequency.exponentialRampToValueAtTime(fb, t0 + 0.06);
        bp.frequency.exponentialRampToValueAtTime(fa * 1.2, t0 + 0.2);
        const fg = ctx.createGain();
        fg.gain.value = g;
        shaper.connect(bp).connect(fg).connect(env);
      }
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 4200;
      env.connect(lp).connect(this.master);
      osc.start(t0);
      osc.stop(t0 + 0.25);

      // --- onset: broadband puff; release: breathy "ff"
      const on = this.noiseSource(t0, 0.1);
      const onBp = ctx.createBiquadFilter();
      onBp.type = 'bandpass';
      onBp.frequency.value = 1700;
      onBp.Q.value = 0.9;
      const onG = ctx.createGain();
      onG.gain.setValueAtTime(0.0001, t0);
      onG.gain.exponentialRampToValueAtTime(loud * 0.45, t0 + 0.008);
      onG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.07);
      on.connect(onBp).connect(onG).connect(this.master);

      const off = this.noiseSource(t0 + 0.11, 0.14);
      const offHp = ctx.createBiquadFilter();
      offHp.type = 'highpass';
      offHp.frequency.value = 2800;
      const offG = ctx.createGain();
      offG.gain.setValueAtTime(0.0001, t0 + 0.11);
      offG.gain.exponentialRampToValueAtTime(loud * 0.14, t0 + 0.14);
      offG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
      off.connect(offHp).connect(offG).connect(this.master);
    }
  }

  /** Play a recorded clip of any kind (growl, squirrel, cat…); silently does nothing without one. */
  play(kind: Kind, gain = 0.6, delay = 0): boolean {
    if (!this.ctx) return false;
    return this.playClip(kind, this.ctx.currentTime + delay, gain * this.distanceGain, 0.96 + Math.random() * 0.08);
  }

  /** Kibble: a recorded chewing clip if there is one; otherwise silence (the old synth clicked). */
  crunch(): void {
    if (!this.ctx || !this.clips.crunch.length) return;
    if (this.ctx.currentTime < this.crunchBusyUntil) return; // one clip at a time
    this.playClip('crunch', this.ctx.currentTime, 0.5 * this.distanceGain, 0.95 + Math.random() * 0.1);
    this.crunchBusyUntil = this.ctx.currentTime + 2.2;
  }

  /** Panting after exertion; call each frame while she should pant, it paces itself. */
  pant(gain = 0.45): void {
    if (!this.ctx || !this.clips.pant.length) return;
    if (this.ctx.currentTime < this.pantBusyUntil) return;
    this.playClip('pant', this.ctx.currentTime, gain * this.distanceGain, 0.97 + Math.random() * 0.06);
    this.pantBusyUntil = this.ctx.currentTime + 4 + Math.random() * 3;
  }

  private sniffBusyUntil = 0;
  /**
   * Nose-down sniffing; call each frame while she's sniffing, it paces itself (a burst, then a pause).
   * A recorded `sniff-N` clip wins; otherwise a synth: three to five quick inhalations through the
   * nose (band-passed noise, the resonance dropping through each one), then a softer breath out.
   */
  sniff(gain = 0.35): void {
    if (!this.ctx || !this.master || !this.noise) return;
    const ctx = this.ctx;
    if (ctx.currentTime < this.sniffBusyUntil) return;
    const t0 = ctx.currentTime;
    const loud = gain * this.distanceGain;
    if (this.playClip('sniff', t0, loud, 0.92 + Math.random() * 0.16)) {
      this.sniffBusyUntil = t0 + 2.4 + Math.random() * 2.2;
      return;
    }
    const n = 3 + Math.floor(Math.random() * 3);
    const gap = 0.11 + Math.random() * 0.05;
    let t = t0;
    for (let i = 0; i < n; i++) {
      const dur = 0.055 + Math.random() * 0.03;
      const src = this.noiseSource(t, dur + 0.02);
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.Q.value = 1.4;
      const f = 1500 + Math.random() * 700;
      bp.frequency.setValueAtTime(f * 1.25, t);
      bp.frequency.exponentialRampToValueAtTime(f * 0.7, t + dur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(loud * (0.55 + Math.random() * 0.45), t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      src.connect(bp).connect(g).connect(this.master);
      t += gap * (0.85 + Math.random() * 0.3);
    }
    const out = this.noiseSource(t, 0.2);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(1200, t);
    lp.frequency.exponentialRampToValueAtTime(500, t + 0.18);
    const go = ctx.createGain();
    go.gain.setValueAtTime(0.0001, t);
    go.gain.exponentialRampToValueAtTime(loud * 0.35, t + 0.03);
    go.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    out.connect(lp).connect(go).connect(this.master);
    this.sniffBusyUntil = t + 1.0 + Math.random() * 2.0;
  }

  /** Soft rising whine, ~0.6 s. */
  whine(delay = 0): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const t0 = ctx.currentTime + delay;
    const loud = 0.25 * this.distanceGain;
    if (this.playClip('whine', t0, loud, 0.95 + Math.random() * 0.1)) return;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(700, t0);
    osc.frequency.exponentialRampToValueAtTime(1150, t0 + 0.35);
    osc.frequency.exponentialRampToValueAtTime(950, t0 + 0.6);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 9;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 25;
    lfo.connect(lfoGain).connect(osc.frequency);
    // nasal formant makes it less like a synth tone
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1900;
    bp.Q.value = 1.5;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(loud, t0 + 0.08);
    g.gain.setValueAtTime(loud, t0 + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.65);
    osc.connect(bp).connect(g).connect(this.master);
    osc.start(t0);
    lfo.start(t0);
    osc.stop(t0 + 0.7);
    lfo.stop(t0 + 0.7);
  }

  /** A far-off human scream (the studying student): sawtooth sweep with vibrato, ~1.1 s. Not a dog sound. */
  scream(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const t0 = ctx.currentTime;
    const loud = 0.09;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(520, t0);
    osc.frequency.exponentialRampToValueAtTime(880, t0 + 0.25);
    osc.frequency.exponentialRampToValueAtTime(760, t0 + 0.9);
    osc.frequency.exponentialRampToValueAtTime(420, t0 + 1.1);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 7;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 30;
    lfo.connect(lfoGain).connect(osc.frequency);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1400;
    bp.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(loud, t0 + 0.06);
    g.gain.setValueAtTime(loud, t0 + 0.8);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.15);
    osc.connect(bp).connect(g).connect(this.master);
    osc.start(t0);
    lfo.start(t0);
    osc.stop(t0 + 1.2);
    lfo.stop(t0 + 1.2);
  }

  /** A clock-tower bell, struck `count` times a second apart. */
  chime(count: number): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    for (let i = 0; i < Math.max(1, Math.min(12, count)); i++) {
      const t0 = ctx.currentTime + i * 1.1;
      for (const [f, g] of [
        [392, 0.12],
        [784, 0.05],
        [1175, 0.025],
      ] as const) {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = f;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(g, t0 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.6);
        osc.connect(gain).connect(this.master);
        osc.start(t0);
        osc.stop(t0 + 1.7);
      }
    }
  }

  /** Breathy huff/sigh, ~0.35 s — the sound of flopping down. */
  huff(delay = 0): void {
    if (!this.ctx || !this.master || !this.noise) return;
    const ctx = this.ctx;
    const t0 = ctx.currentTime + delay;
    const loud = 0.3 * this.distanceGain;
    if (this.playClip('huff', t0, loud, 0.95 + Math.random() * 0.1)) return;
    const src = this.noiseSource(t0, 0.4);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(900, t0);
    lp.frequency.exponentialRampToValueAtTime(400, t0 + 0.35);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(loud, t0 + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
    src.connect(lp).connect(g).connect(this.master);
  }
}
